import { useInterviewState } from "../state/InterviewProvider";
import { t, type StringKey } from "./strings";
import type { Locale } from "../templates/types";

/**
 * There is deliberately NO second React context here. `InterviewState`
 * already carries `locale` (§8/§15), `InterviewProvider` is already mounted
 * above the router, and the `LOCALE_CHANGED` reducer case already exists
 * (app/frontend/engine/reducer.ts) and keeps all interview data intact when
 * the locale flips. Introducing a parallel `LocaleProvider` would mean two
 * sources of truth for one value. These hooks are a thin convenience over
 * `useInterviewState()` for the handful of files (App.tsx, RunCheckIn.tsx)
 * that sit directly under the provider.
 *
 * Everything further down the tree (ChatPane, FieldCard, SafetyBanner, ...)
 * already receives `locale` — or a `state`/`meta` object that carries it —
 * as a prop, and several of those components are unit-tested by rendering
 * them standalone, outside of any `InterviewProvider`. Those components call
 * the plain `t(locale, key)` export from `./strings` directly rather than
 * this hook, so they keep working without a provider in scope. Both paths
 * read the same table in `strings.ts`, so there is exactly one source of
 * copy either way.
 */
export function useLocale(): Locale {
  return useInterviewState().state.locale;
}

export function useSetLocale(): (locale: Locale) => void {
  const { dispatch } = useInterviewState();
  return (locale: Locale) => dispatch({ type: "LOCALE_CHANGED", locale });
}

export function useT(): (key: StringKey, vars?: Record<string, string | number>) => string {
  const locale = useLocale();
  return (key: StringKey, vars?: Record<string, string | number>) => t(locale, key, vars);
}
